import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { format } from "date-fns";
import {
  AlertCircle,
  FileText,
  MessageSquare,
  RefreshCw,
  Mail,
  ClipboardCheck,
  MoreHorizontal,
  Activity,
  ExternalLink,
  Loader2,
  RotateCcw,
  ShieldAlert,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/page-header";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePageTitle } from "@/hooks/use-page-title";
import { useAuth } from "@/lib/auth-context";
import { cn } from "@/lib/utils";
import { ReportDiagnosticsDrawer, type ReportDiagnostics } from "@/components/report-diagnostics-drawer";
import { BehaviorDiagnosticsDrawer, type BehaviorDiagnostics } from "@/components/behavior-diagnostics-drawer";

/**
 * Gate I9.2 Phase 2 (C) + P28a: Parse and format EMAIL_INGEST_HOLD and INGEST_HOLD review reasons.
 * Handles semicolon-delimited reason strings that may contain multiple hold flags.
 */
function parseEmailIngestHoldReason(reason: string | null): {
  isEmailIngestHold: boolean;
  isIngestGateHold: boolean;
  description: string | null;
} {
  if (!reason) return { isEmailIngestHold: false, isIngestGateHold: false, description: null };

  const parts = reason.split(";").map((s) => s.trim());

  const isEmailIngestHold = parts.some(
    (p) => p.startsWith("EMAIL_INGEST_HOLD") || p.includes("EMAIL_INGEST_HOLD")
  );
  const isIngestGateHold = parts.some((p) => p.startsWith("INGEST_HOLD:"));

  const otherParts = parts.filter(
    (p) =>
      !p.startsWith("EMAIL_INGEST_HOLD") &&
      !p.includes("EMAIL_INGEST_HOLD") &&
      !p.startsWith("INGEST_HOLD:")
  );

  return {
    isEmailIngestHold,
    isIngestGateHold,
    description: otherParts.length > 0 ? otherParts.join("; ") : null,
  };
}

/**
 * Parse diagnostic messages for interaction reports with missing scores
 */
function parseInteractionDiagnostic(reason: string | null): {
  isScoreDiagnostic: boolean;
  count: number;
  animalIds: string[];
  fullMessage: string | null;
} {
  if (!reason) return { isScoreDiagnostic: false, count: 0, animalIds: [], fullMessage: null };

  const scoreMatch = reason.match(/(\d+)\s+interaction\(s\)\s+have\s+no\s+score:\s*(.+)/i);
  if (scoreMatch) {
    const count = parseInt(scoreMatch[1], 10);
    const idsString = scoreMatch[2];
    const animalIds = idsString.split(",").map((id) => id.trim()).filter(Boolean);
    return { isScoreDiagnostic: true, count, animalIds, fullMessage: reason };
  }

  return { isScoreDiagnostic: false, count: 0, animalIds: [], fullMessage: reason };
}

interface PendingReportItem {
  id: string;
  sourceReportType: string;
  sourceReportId: string;
  reviewStatus: string;
  reviewReason: string | null;
  parsedData: {
    location?: string;
    reportDate?: string;
    animals?: unknown[];
    interactions?: unknown[];
  };
  parseDiagnostics: unknown;
  editHistory: Array<{
    field: string;
    itemIndex?: number;
    oldValue: unknown;
    newValue: unknown;
    editedAt: string;
    editedBy: string;
  }>;
  createdAt: string;
  updatedAt: string;
  sourceEmailId?: string | null;
}

interface PendingReportsResponse {
  items: PendingReportItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ReparseCounts {
  all_pets: number;
  interaction: number;
}

interface PreviewRow {
  pendingId: string;
  locationName: string;
  reportDate: string;
  currentReviewReason: string;
}

interface ReparseRunResult {
  processed: number;
  remaining: number;
  reparsed: number;
  autoAccepted: number;
  skipped: number;
  results: unknown[];
}

type ReparseStep =
  | "idle"
  | "loading-counts"
  | "select-type"
  | "confirm"
  | "loading-preview"
  | "preview"
  | "running"
  | "results";

export default function ReviewQueuePage() {
  usePageTitle("Review Queue");
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  const { data: metaData } = useQuery<{ ingestHoldEnabled: boolean; ingestHoldReason?: string | null }>({
    queryKey: ["/api/app-metadata"],
  });

  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [diagnosticsItem, setDiagnosticsItem] = useState<PendingReportItem | null>(null);
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);

  // ── P28 Reparse state ────────────────────────────────────────────────────
  const [reparseStep, setReparseStep] = useState<ReparseStep>("idle");
  const [reparseCounts, setReparseCounts] = useState<ReparseCounts | null>(null);
  const [reparseType, setReparseType] = useState<"all_pets" | "interaction" | null>(null);
  const [previewData, setPreviewData] = useState<{
    rows: PreviewRow[];
    remaining: number;
  } | null>(null);
  const [reparseResults, setReparseResults] = useState<ReparseRunResult | null>(null);
  const [reparseError, setReparseError] = useState<string | null>(null);

  const openDiagnostics = (item: PendingReportItem) => {
    setDiagnosticsItem(item);
    setDiagnosticsOpen(true);
  };

  const getPdfUrl = (item: PendingReportItem) => {
    if (item.sourceReportType === "interaction") {
      return `/api/behavior/reports/${item.sourceReportId}/file`;
    }
    return `/api/reports/${item.sourceReportId}/file`;
  };

  const buildPendingUrl = () => {
    const params = new URLSearchParams();
    params.set("status", "pending");
    params.set("limit", "200");
    if (typeFilter !== "all") {
      params.set("type", typeFilter);
    }
    return `/api/reports/pending?${params.toString()}`;
  };

  const { data, isLoading, isError, refetch, isFetching } = useQuery<PendingReportsResponse>({
    queryKey: ["/api/reports/pending", typeFilter],
    queryFn: async () => {
      const res = await fetch(buildPendingUrl(), { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch pending reports");
      return res.json();
    },
  });

  const getReportTypeLabel = (type: string) => {
    switch (type) {
      case "all_pets":
        return "All Pets";
      case "interaction":
        return "Interaction";
      default:
        return type;
    }
  };

  const getReportTypeIcon = (type: string) => {
    switch (type) {
      case "all_pets":
        return <FileText className="h-4 w-4" />;
      case "interaction":
        return <MessageSquare className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getItemCount = (item: PendingReportItem) => {
    if (item.sourceReportType === "all_pets") {
      return item.parsedData?.animals?.length || 0;
    } else if (item.sourceReportType === "interaction") {
      return item.parsedData?.interactions?.length || 0;
    }
    return 0;
  };

  const getBatchSize = () => {
    if (reparseType && reparseCounts) {
      return Math.max(reparseCounts[reparseType], 1);
    }
    return reparseType === "all_pets" ? 10 : 20;
  };

  const handleCloseReparseDialog = () => {
    setReparseStep("idle");
    setReparseCounts(null);
    setReparseType(null);
    setPreviewData(null);
    setReparseResults(null);
    setReparseError(null);
  };

  const handleReparseClick = async () => {
    setReparseStep("loading-counts");
    setReparseError(null);
    try {
      const res = await fetch("/api/admin/reparse-pending-reports/counts", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch pending counts");
      const counts: ReparseCounts & { ok: boolean } = await res.json();
      setReparseCounts({ all_pets: counts.all_pets, interaction: counts.interaction });

      if (counts.all_pets > 0 && counts.interaction > 0) {
        setReparseStep("select-type");
      } else if (counts.interaction > 0) {
        setReparseType("interaction");
        setReparseStep("confirm");
      } else if (counts.all_pets > 0) {
        setReparseType("all_pets");
        setReparseStep("confirm");
      } else {
        setReparseStep("idle");
        setReparseError("No pending reports to reparse.");
      }
    } catch (err) {
      setReparseStep("idle");
      setReparseError(err instanceof Error ? err.message : "Failed to fetch counts");
    }
  };

  const handlePreview = async () => {
    if (!reparseType) return;
    setReparseStep("loading-preview");
    setReparseError(null);
    try {
      const result = await apiRequest("POST", "/api/admin/reparse-pending-reports", {
        type: reparseType,
        batch: getBatchSize(),
        dryRun: true,
      });
      setPreviewData({ rows: result.rows || [], remaining: result.remaining ?? 0 });
      setReparseStep("preview");
    } catch (err) {
      setReparseStep("confirm");
      setReparseError(err instanceof Error ? err.message : "Failed to load preview");
    }
  };

  const handleRunReparse = async () => {
    if (!reparseType) return;
    setReparseStep("running");
    setReparseError(null);
    try {
      const result = await apiRequest("POST", "/api/admin/reparse-pending-reports", {
        type: reparseType,
        batch: getBatchSize(),
        dryRun: false,
      });
      setReparseResults({
        processed: result.processed,
        remaining: result.remaining,
        reparsed: result.reparsed,
        autoAccepted: result.autoAccepted,
        skipped: result.skipped,
        results: result.results || [],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/reports/pending", typeFilter] });
      setReparseStep("results");
    } catch (err) {
      setReparseStep("confirm");
      setReparseError(err instanceof Error ? err.message : "Reparse failed");
    }
  };

  const handleRunNextBatch = () => {
    setPreviewData(null);
    setReparseResults(null);
    setReparseError(null);
    setReparseStep("confirm");
  };

  return (
    <div className="container mx-auto py-6 px-4 space-y-6">
      <PageHeader
        breadcrumb={[
          { label: "Reporting" },
          { label: "Reports", href: "/reports" },
          { label: "Review Queue" },
        ]}
        title="Review Queue"
      />

      {metaData?.ingestHoldEnabled && (
        <div
          className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300"
          data-testid="banner-ingest-gate"
        >
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <p className="text-sm">
            <span className="font-semibold">Ingest gate active.</span>{" "}
            {metaData.ingestHoldReason
              ? metaData.ingestHoldReason
              : "All new PDF reports are being held for manual review."}
          </p>
        </div>
      )}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
          <div>
            <CardTitle className="text-lg">Pending Reports</CardTitle>
            <CardDescription>
              {data?.pagination.total || 0} report{(data?.pagination.total || 0) !== 1 ? "s" : ""} awaiting review
            </CardDescription>
            <p className="text-xs text-muted-foreground mt-1" data-testid="text-review-order-hint">
              Sorted oldest first — reviewing in date order is recommended, but not required.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[160px]" data-testid="select-type-filter">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="all_pets">All Pets</SelectItem>
                <SelectItem value="interaction">Interaction</SelectItem>
              </SelectContent>
            </Select>
            {isSuperAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReparseClick}
                disabled={isFetching || (data?.pagination.total || 0) === 0}
                data-testid="button-reparse-pending"
              >
                <RotateCcw className="h-4 w-4 mr-1.5" />
                Reparse pending
              </Button>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetch()}
              disabled={isFetching}
              data-testid="button-refresh"
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="flex items-center gap-2 p-4 text-destructive bg-destructive/10 rounded-md" data-testid="text-error">
              <AlertCircle className="h-5 w-5" />
              <span>Failed to load pending reports. Please try again.</span>
            </div>
          ) : data?.items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="text-empty">
              <FileText className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No pending reports</p>
              <p className="text-sm">All reports have been reviewed</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Report Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Edits</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-[160px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.items || []).map((item) => {
                  const hasReparseSentinel = item.editHistory?.some(
                    (e) => e.field === "__system_reparse__",
                  );
                  const lastReparse = hasReparseSentinel
                    ? (() => {
                        for (let i = item.editHistory.length - 1; i >= 0; i--) {
                          if (item.editHistory[i].field === "__system_reparse__")
                            return item.editHistory[i];
                        }
                        return null;
                      })()
                    : null;

                  return (
                    <TableRow key={item.id} data-testid={`row-pending-${item.id}`}>
                      <TableCell>
                        {item.parsedData?.reportDate || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="gap-1">
                          {getReportTypeIcon(item.sourceReportType)}
                          {getReportTypeLabel(item.sourceReportType)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {item.sourceEmailId ? (
                          <Badge variant="secondary" className="gap-1 text-xs" data-testid={`badge-email-ingest-${item.id}`}>
                            <Mail className="h-3 w-3" />
                            Email ingest
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">Manual upload</span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">
                        {item.parsedData?.location || "—"}
                      </TableCell>
                      <TableCell>
                        {getItemCount(item)}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <div className="flex flex-wrap items-center gap-1">
                          {(() => {
                            const parsed = parseEmailIngestHoldReason(item.reviewReason);
                            const holdBadges: React.ReactNode[] = [];

                            if (parsed.isEmailIngestHold) {
                              holdBadges.push(
                                <Badge key="email-hold" variant="outline" className="shrink-0 text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800" data-testid={`badge-email-hold-${item.id}`}>
                                  Email review
                                </Badge>
                              );
                            }

                            if (parsed.isIngestGateHold) {
                              holdBadges.push(
                                <Badge key="ingest-gate" variant="outline" className="shrink-0 text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:border-amber-800" data-testid={`badge-ingest-gate-${item.id}`}>
                                  Ingest gate hold
                                </Badge>
                              );
                            }

                            if (holdBadges.length > 0) return <>{holdBadges}</>;

                            const scoreDiag = parseInteractionDiagnostic(item.reviewReason);
                            if (scoreDiag.isScoreDiagnostic) {
                              return (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="outline" className="shrink-0 text-xs bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-400 dark:border-blue-800 cursor-help" data-testid={`badge-score-diagnostic-${item.id}`}>
                                      {scoreDiag.count} missing score{scoreDiag.count !== 1 ? "s" : ""}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-sm">
                                    <p className="text-sm font-medium mb-1">Animals without scores:</p>
                                    <p className="text-xs text-muted-foreground">{scoreDiag.animalIds.join(", ")}</p>
                                  </TooltipContent>
                                </Tooltip>
                              );
                            }

                            return (
                              <span className="line-clamp-1 break-all text-muted-foreground">
                                {item.reviewReason || "—"}
                              </span>
                            );
                          })()}
                          {hasReparseSentinel && lastReparse && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="secondary" className="text-xs shrink-0" data-testid={`badge-reparsed-${item.id}`}>
                                  Reparsed
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                {format(new Date(lastReparse.editedAt), "MM/dd/yyyy HH:mm")}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.editHistory?.length > 0 ? (
                          <Badge variant="secondary">{item.editHistory.length}</Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(item.createdAt), "MM/dd/yy")}
                      </TableCell>
                      <TableCell>
                        {item.sourceReportType === "interaction" && !item.sourceReportId ? (
                          <span className="text-xs text-muted-foreground italic">
                            Missing source report reference
                          </span>
                        ) : (
                          <div className="flex items-center gap-1">
                            <Button variant="outline" size="sm" className="text-warning border-warning/30" asChild data-testid={`button-review-${item.id}`}>
                              <Link href={`/reports/review/${item.sourceReportType}/${
                                item.sourceReportType === "interaction" ? item.sourceReportId : item.id
                              }`}>
                                <ClipboardCheck className="h-4 w-4 mr-1.5" />
                                Review
                              </Link>
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`button-actions-${item.id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem asChild>
                                  <a
                                    href={getPdfUrl(item)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`menu-view-pdf-${item.id}`}
                                  >
                                    <FileText className="h-4 w-4 mr-2" />
                                    View PDF
                                    <ExternalLink className="h-3.5 w-3.5 ml-auto opacity-50" />
                                  </a>
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => openDiagnostics(item)}
                                  data-testid={`menu-diagnostics-${item.id}`}
                                >
                                  <Activity className="h-4 w-4 mr-2" />
                                  Diagnostics
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {diagnosticsItem?.sourceReportType === "all_pets" && (
        <ReportDiagnosticsDrawer
          open={diagnosticsOpen}
          onOpenChange={(open) => {
            setDiagnosticsOpen(open);
            if (!open) setDiagnosticsItem(null);
          }}
          diagnostics={diagnosticsItem.parseDiagnostics as ReportDiagnostics}
          location={diagnosticsItem.parsedData?.location || "Unknown"}
          reportDate={diagnosticsItem.parsedData?.reportDate || "Unknown"}
        />
      )}

      {diagnosticsItem?.sourceReportType === "interaction" && (
        <BehaviorDiagnosticsDrawer
          open={diagnosticsOpen}
          onOpenChange={(open) => {
            setDiagnosticsOpen(open);
            if (!open) setDiagnosticsItem(null);
          }}
          diagnostics={diagnosticsItem.parseDiagnostics as BehaviorDiagnostics}
          location={diagnosticsItem.parsedData?.location || "Unknown"}
          reportDate={diagnosticsItem.parsedData?.reportDate || "Unknown"}
        />
      )}

      {/* P28: Multi-step bulk reparse dialog */}
      <Dialog
        open={reparseStep !== "idle"}
        onOpenChange={(open) => !open && handleCloseReparseDialog()}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Reparse Pending Reports</DialogTitle>
            {reparseStep === "select-type" && (
              <DialogDescription>
                Both report types have pending reports. Select which to reparse.
              </DialogDescription>
            )}
            {reparseStep === "confirm" && reparseType && reparseCounts && (
              <DialogDescription>
                {reparseCounts[reparseType]} {reparseType === "all_pets" ? "all-pets" : "interaction"} report{reparseCounts[reparseType] !== 1 ? "s" : ""} pending
              </DialogDescription>
            )}
            {reparseStep === "preview" && previewData && (
              <DialogDescription>
                {previewData.rows.length} report{previewData.rows.length !== 1 ? "s" : ""} in this batch
                {previewData.remaining > 0 ? ` · ${previewData.remaining} more after` : ""}
              </DialogDescription>
            )}
            {reparseStep === "results" && reparseResults && (
              <DialogDescription>
                {reparseResults.processed} processed · {reparseResults.remaining} still pending
              </DialogDescription>
            )}
          </DialogHeader>

          {/* Loading counts */}
          {reparseStep === "loading-counts" && (
            <div className="flex items-center justify-center py-8 gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Fetching counts…</span>
            </div>
          )}

          {/* Select type */}
          {reparseStep === "select-type" && reparseCounts && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setReparseType("interaction")}
                  className={cn(
                    "border rounded-md p-4 text-left transition-colors",
                    reparseType === "interaction"
                      ? "border-primary bg-primary/5"
                      : "border-border hover-elevate",
                  )}
                  data-testid="button-select-interaction"
                >
                  <p className="font-medium text-sm">Interaction</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {reparseCounts.interaction} pending
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setReparseType("all_pets")}
                  className={cn(
                    "border rounded-md p-4 text-left transition-colors",
                    reparseType === "all_pets"
                      ? "border-primary bg-primary/5"
                      : "border-border hover-elevate",
                  )}
                  data-testid="button-select-all-pets"
                >
                  <p className="font-medium text-sm">All Pets</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {reparseCounts.all_pets} pending
                  </p>
                </button>
              </div>
              {reparseError && (
                <p className="text-sm text-destructive" data-testid="text-reparse-error">{reparseError}</p>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={handleCloseReparseDialog} data-testid="button-reparse-cancel">
                  Cancel
                </Button>
                <Button
                  onClick={() => reparseType && setReparseStep("confirm")}
                  disabled={!reparseType}
                  data-testid="button-reparse-next"
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {/* Confirm */}
          {reparseStep === "confirm" && reparseType && (
            <div className="space-y-4 py-2">
              <p className="text-sm">
                This will re-fetch each PDF from storage and re-run the parser for up to{" "}
                <span className="font-medium">{getBatchSize()}</span>{" "}
                <span className="font-medium">
                  {reparseType === "interaction" ? "interaction" : "all-pets"}
                </span>{" "}
                reports.
              </p>
              <p className="text-sm text-destructive">
                Manual edits on those reports will be overwritten.
              </p>
              {reparseError && (
                <p className="text-sm text-destructive" data-testid="text-reparse-error">{reparseError}</p>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={handleCloseReparseDialog} data-testid="button-reparse-cancel">
                  Cancel
                </Button>
                <Button variant="outline" onClick={handlePreview} data-testid="button-reparse-preview">
                  Preview
                </Button>
                <Button onClick={handleRunReparse} data-testid="button-reparse-run">
                  Reparse {getBatchSize()}
                </Button>
              </div>
            </div>
          )}

          {/* Loading preview */}
          {reparseStep === "loading-preview" && (
            <div className="flex items-center justify-center py-8 gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Loading preview…</span>
            </div>
          )}

          {/* Preview */}
          {reparseStep === "preview" && previewData && (
            <div className="space-y-4 py-2">
              <div className="max-h-[240px] overflow-y-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Location</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Date</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Current Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.rows.map((row) => (
                      <tr key={row.pendingId} className="border-b last:border-0">
                        <td className="px-3 py-2">{row.locationName}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{row.reportDate}</td>
                        <td className="px-3 py-2 max-w-[180px] truncate text-muted-foreground">
                          {row.currentReviewReason || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {reparseError && (
                <p className="text-sm text-destructive" data-testid="text-reparse-error">{reparseError}</p>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={handleCloseReparseDialog} data-testid="button-reparse-cancel">
                  Cancel
                </Button>
                <Button onClick={handleRunReparse} data-testid="button-reparse-run">
                  Reparse {previewData.rows.length}
                </Button>
              </div>
            </div>
          )}

          {/* Running */}
          {reparseStep === "running" && (
            <div className="flex items-center justify-center py-8 gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Reparsing reports…</span>
            </div>
          )}

          {/* Results */}
          {reparseStep === "results" && reparseResults && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="border rounded-md p-3 text-center">
                  <p className="text-2xl font-bold">{reparseResults.processed}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Processed</p>
                </div>
                <div className="border rounded-md p-3 text-center">
                  <p className="text-2xl font-bold">{reparseResults.reparsed}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Reparsed</p>
                </div>
                <div className="border rounded-md p-3 text-center">
                  <p className="text-2xl font-bold">{reparseResults.autoAccepted}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Auto-accepted</p>
                </div>
                <div className="border rounded-md p-3 text-center">
                  <p className="text-2xl font-bold">{reparseResults.skipped}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Errors</p>
                </div>
              </div>
              {reparseError && (
                <p className="text-sm text-destructive" data-testid="text-reparse-error">{reparseError}</p>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={handleCloseReparseDialog} data-testid="button-reparse-close">
                  Close
                </Button>
                {reparseResults.remaining > 0 && (
                  <Button onClick={handleRunNextBatch} data-testid="button-reparse-next-batch">
                    Run next batch
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
